export const PWA_UPDATE_AVAILABLE_EVENT = "viewr:pwaUpdateAvailable";
export const PWA_UPDATE_RELOAD_GUARD_KEY = "viewr:pwaUpdateReloadGuard";

const RELOAD_GUARD_TTL_MS = 10_000;
const CONTROLLER_CHANGE_WAIT_MS = 5_000;

export interface PwaUpdateEventDetail {
  buildId?: string;
}

interface ApplyPwaUpdateOptions {
  beforeReload?: () => Promise<void>;
  buildId?: string;
  now?: () => number;
  registration?: ServiceWorkerRegistration | null;
  reload?: () => void;
  serviceWorker?: ServiceWorkerContainer;
  session?: Pick<Storage, "getItem" | "removeItem" | "setItem">;
  timeoutMs?: number;
}

let currentRegistration: ServiceWorkerRegistration | null = null;
let pendingUpdateDetail: PwaUpdateEventDetail | null = null;
let updateApplying = false;

export const setPwaRegistration = (
  registration: ServiceWorkerRegistration,
): void => {
  currentRegistration = registration;
};

export const requestPwaUpdate = (detail: PwaUpdateEventDetail = {}): void => {
  pendingUpdateDetail = detail;
  window.dispatchEvent(
    new CustomEvent<PwaUpdateEventDetail>(PWA_UPDATE_AVAILABLE_EVENT, {
      detail,
    }),
  );
};

export const getPendingPwaUpdate = (): PwaUpdateEventDetail | null =>
  pendingUpdateDetail;

export const clearPendingPwaUpdate = (): void => {
  pendingUpdateDetail = null;
};

export const observePwaRegistration = (
  registration: ServiceWorkerRegistration,
  serviceWorker: ServiceWorkerContainer = navigator.serviceWorker,
): void => {
  setPwaRegistration(registration);

  if (registration.waiting) {
    requestPwaUpdate();
  }

  registration.addEventListener("updatefound", () => {
    const installing = registration.installing;
    if (!installing) return;

    installing.addEventListener("statechange", () => {
      if (installing.state === "installed" && serviceWorker.controller) {
        requestPwaUpdate();
      }
    });
  });
};

const waitForControllerChange = (
  serviceWorker: ServiceWorkerContainer,
  timeoutMs: number,
): Promise<void> =>
  new Promise((resolve) => {
    const timeout = window.setTimeout(() => {
      serviceWorker.removeEventListener("controllerchange", onChange);
      resolve();
    }, timeoutMs);
    const onChange = () => {
      window.clearTimeout(timeout);
      serviceWorker.removeEventListener("controllerchange", onChange);
      resolve();
    };
    serviceWorker.addEventListener("controllerchange", onChange);
  });

export const applyPwaUpdate = async ({
  beforeReload,
  buildId = "service-worker",
  now = Date.now,
  registration = currentRegistration,
  reload = () => window.location.reload(),
  serviceWorker =
    typeof navigator !== "undefined" && "serviceWorker" in navigator
      ? navigator.serviceWorker
      : undefined,
  session = sessionStorage,
  timeoutMs = CONTROLLER_CHANGE_WAIT_MS,
}: ApplyPwaUpdateOptions = {}): Promise<boolean> => {
  if (updateApplying) return false;

  const guardValue = session.getItem(PWA_UPDATE_RELOAD_GUARD_KEY);
  if (guardValue) {
    const [guardBuildId, guardTimestamp] = guardValue.split(":");
    if (
      guardBuildId === buildId &&
      now() - Number(guardTimestamp) < RELOAD_GUARD_TTL_MS
    ) {
      return false;
    }
  }

  updateApplying = true;
  try {
    await beforeReload?.();
    session.setItem(
      PWA_UPDATE_RELOAD_GUARD_KEY,
      `${buildId}:${now()}`,
    );

    try {
      await registration?.update();
    } catch {
      // version.json already confirmed a newer deployment. Continue with a
      // network-first reload even if the explicit worker check fails.
    }

    const waiting = registration?.waiting;
    if (waiting && serviceWorker) {
      const controllerChanged = waitForControllerChange(
        serviceWorker,
        timeoutMs,
      );
      waiting.postMessage({ type: "SKIP_WAITING" });
      await controllerChanged;
    }

    reload();
    return true;
  } catch (error) {
    session.removeItem(PWA_UPDATE_RELOAD_GUARD_KEY);
    throw error;
  } finally {
    updateApplying = false;
  }
};
